/**
 * Open Banking Integration — TrueLayer Auth Dialog + Data API
 *
 * Lets UK users connect their bank accounts and pull transaction data
 * into Adulting's Money statements flow using TrueLayer's hosted
 * auth dialog for provider selection and consent collection.
 *
 * The redirect flow and API calls go through Supabase Edge Functions
 * so the TrueLayer client secret stays off the client.
 */

import { supabase } from '@/integrations/supabase/client';

export interface BankTransaction {
  transactionId: string;
  bookingDate: string;          // YYYY-MM-DD
  valueDate?: string;
  transactionAmount: {
    amount: string;             // e.g. "-12.50"
    currency: string;           // "GBP"
  };
  creditorName?: string;
  debtorName?: string;
  remittanceInformationUnstructured?: string;
}

export interface ConnectedAccount {
  id: string;
  iban?: string;
  name?: string;
  account_type?: string | null;
  account_role?: string | null;
  currency?: string;
  balance?: string;
  institution_id: string;
  institution_name: string;
  last_synced: string;
}

type StoredBankTransactionFetchOptions = {
  fromDate?: string;
  negativeOnly?: boolean;
  pageSize?: number;
  select?: string;
};

// Points to the deployed Supabase edge function base URL
const EDGE_BASE = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1`;
const SUPABASE_PUBLISHABLE_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;

async function buildFunctionHeaders() {
  const { data: { session } } = await supabase.auth.getSession();

  if (!session?.access_token) {
    throw new Error('You need to be signed in to connect a bank account.');
  }

  return {
    'Content-Type': 'application/json',
    apikey: SUPABASE_PUBLISHABLE_KEY,
    Authorization: `Bearer ${session.access_token}`,
  };
}

/**
 * Step 1 — Initiate bank connection.
 * Calls the edge function which builds a TrueLayer auth link for the
 * hosted auth dialog and returns the URL the user should follow.
 */
export async function initiateBankConnection(
  redirectUrl: string,
  userId: string
): Promise<{ authUrl: string; state: string }> {
  const headers = await buildFunctionHeaders();
  const response = await fetch(`${EDGE_BASE}/open-banking-connect`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ redirectUrl, userId }),
  });

  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error ?? `Connection failed: ${response.status}`);
  }

  return response.json();
}

/**
 * Step 2 — Complete bank connection after OAuth redirect.
 * Called when the user lands back on /money?tab=budget&code=...&state=...
 *
 * The edge function exchanges the auth code and fetches recent transactions from TrueLayer
 * and saves them to Supabase tables.
 */
export async function completeBankConnection(
  code: string,
  state: string,
  redirectUrl: string,
  userId: string,
  callbackUrl?: string
): Promise<{ accounts: ConnectedAccount[]; transactionCount: number }> {
  const headers = await buildFunctionHeaders();
  const response = await fetch(`${EDGE_BASE}/open-banking-callback`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ code, state, redirectUrl, userId, callbackUrl }),
  });

  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error ?? `Callback failed: ${response.status}`);
  }

  return response.json();
}

/**
 * Fetch transactions for a connected account (already stored in Supabase).
 * This is a direct Supabase query — use supabase client in the component.
 */
export async function fetchStoredBankTransactions(
  userId: string,
  options: StoredBankTransactionFetchOptions = {},
) {
  const pageSize = Math.max(100, Math.min(options.pageSize || 500, 1000));
  const rows: any[] = [];
  let from = 0;

  while (true) {
    let query = supabase
      .from('bank_transactions' as any)
      .select(options.select || '*')
      .eq('user_id', userId)
      .order('booking_date', { ascending: false })
      .range(from, from + pageSize - 1);

    if (options.fromDate) {
      query = query.gte('booking_date', options.fromDate);
    }

    if (options.negativeOnly) {
      query = query.lt('amount', 0);
    }

    const { data, error } = await query;
    if (error) {
      throw error;
    }

    const batch = (data || []) as any[];
    rows.push(...batch);
    if (batch.length < pageSize) {
      break;
    }

    from += pageSize;
  }

  return rows;
}

/** True if open banking is enabled via env flag */
export function isOpenBankingEnabled(): boolean {
  return import.meta.env.VITE_OPEN_BANKING_ENABLED === 'true';
}
